import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import type { CSSProperties } from "react";
import { useEffect, useMemo, useState } from "react";

type StatementLog = {
  id: string;
  familyId: string;
  familyName: string;
  familyCode: string | null;
  schoolYear: string;
  statementMonth: string;
  triggerType: "scheduled" | "manual_resend";
  initiatedByUid?: string | null;
  initiatedByEmail?: string | null;
  recipients: string[];
  status: "sent" | "failed" | "skipped";
  reason?: string | null;
  approvedHours: number;
  pendingHours: number;
  goalHours: number;
  remainingHours: number;
  entriesCountThisMonth: number;
  createdAt?: { _seconds?: number; seconds?: number } | null;
};

type FamilySummary = {
  familyId: string;
  familyName: string;
  familyCode: string;
  requireVolunteerApproval: boolean;
  volunteerGoalHoursOverride?: number | null;
  totals: {
    approvedHours: number;
    pendingHours: number;
    rejectedHours: number;
    goalHours: number;
    remainingHours: number;
    percentComplete: number;
    completedGoal: boolean;
  };
  statement: {
    statementMonth: string;
    monthlyEntriesCount: number;
    monthlyApprovedHours: number;
    monthlyPendingHours: number;
    monthlyBreakdown?: {
      month: string;
      approvedHours: number;
      pendingHours: number;
      entriesCount: number;
    }[];
    recipients: string[];
    lastLog: StatementLog | null;
  };
  flags: {
    noActivity: boolean;
    nearGoal: boolean;
  };
  meta: {
    studentCount: number;
    memberCount: number;
    parentEmails: string[];
  };
};

type MonthlyTrend = {
  month: string;
  approvedHours: number;
  pendingHours: number;
  entriesCount: number;
};

type Drilldown = {
  title: string;
  description: string;
  families: FamilySummary[];
  logs: StatementLog[];
};

function functionsClient() {
  return getFunctions(getApp(), "us-central1");
}

function previousMonthKey(now = new Date()) {
  const dt = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function schoolYearMonths(yearLabel: string) {
  const match = /^(\d{4})-(\d{4})$/.exec(yearLabel.trim());
  if (!match) return [];
  const startYear = Number(match[1]);
  const endYear = Number(match[2]);
  if (!Number.isFinite(startYear) || !Number.isFinite(endYear) || endYear !== startYear + 1) return [];

  return [
    ...Array.from({ length: 6 }, (_, i) => `${startYear}-${String(i + 7).padStart(2, "0")}`),
    ...Array.from({ length: 6 }, (_, i) => `${endYear}-${String(i + 1).padStart(2, "0")}`),
  ];
}

function defaultMonthForSchoolYear(yearLabel: string) {
  const months = schoolYearMonths(yearLabel);
  const previousMonth = previousMonthKey();
  if (months.includes(previousMonth)) return previousMonth;
  return months[months.length - 1] || previousMonth;
}

function monthLabel(monthKey: string) {
  const [year, month] = monthKey.split("-").map(Number);
  if (!year || !month) return monthKey;
  return new Date(year, month - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
}

function tsToString(ts?: { _seconds?: number; seconds?: number } | null) {
  const seconds = ts?._seconds ?? ts?.seconds;
  if (!seconds) return "-";
  return new Date(seconds * 1000).toLocaleString();
}

function statusBadge(status: string) {
  if (status === "sent") return "Sent";
  if (status === "failed") return "Failed";
  if (status === "skipped") return "Skipped";
  return status;
}

function num(value: number) {
  return Number.isFinite(value) ? value : 0;
}

function pct(value: number, total: number) {
  if (!total || total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((value / total) * 100)));
}

function hours(value: number) {
  return num(value).toFixed(2);
}

function compactList(values: string[], fallback = "No recipients") {
  if (!values.length) return fallback;
  if (values.length <= 2) return values.join(", ");
  return `${values.slice(0, 2).join(", ")} +${values.length - 2} more`;
}

function KpiTile({
  label,
  value,
  detail,
  tone = "blue",
  onClick,
}: {
  label: string;
  value: string | number;
  detail?: string;
  tone?: "blue" | "gold" | "green" | "red";
  onClick?: () => void;
}) {
  return (
    <button className={`volunteer-kpi volunteer-kpi-${tone}`} type="button" onClick={onClick}>
      <b>{value}</b>
      <span>{label}</span>
      {detail ? <small>{detail}</small> : null}
    </button>
  );
}

function MeterBar({
  label,
  value,
  total,
  detail,
  tone = "blue",
  onClick,
}: {
  label: string;
  value: number;
  total: number;
  detail?: string;
  tone?: "blue" | "gold" | "green" | "red";
  onClick?: () => void;
}) {
  const width = pct(value, total);
  const body = (
    <>
      <div className="volunteer-meter-top">
        <span>{label}</span>
        <b>{detail || `${hours(value)} / ${hours(total)}`}</b>
      </div>
      <div className="volunteer-meter-track">
        <div className={`volunteer-meter-fill volunteer-meter-${tone}`} style={{ width: `${width}%` }} />
      </div>
    </>
  );
  if (onClick) {
    return (
      <button className="volunteer-meter volunteer-clickable-row" type="button" onClick={onClick}>
        {body}
      </button>
    );
  }

  return (
    <div className="volunteer-meter">
      {body}
    </div>
  );
}

function Donut({ value, total, label, tone = "blue" }: { value: number; total: number; label: string; tone?: "blue" | "gold" | "green" | "red" }) {
  const width = pct(value, total);
  return (
    <div className={`volunteer-donut volunteer-donut-${tone}`} style={{ "--pct": `${width}%` } as CSSProperties}>
      <div>
        <b>{width}%</b>
        <span>{label}</span>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: StatementLog["status"] }) {
  const tone = status === "sent" ? "ok" : status === "failed" ? "bad" : "warn";
  return <span className={`volunteer-pill volunteer-pill-${tone}`}>{statusBadge(status)}</span>;
}

function familyMonthBucket(family: FamilySummary, month: string) {
  return family.statement.monthlyBreakdown?.find((row) => row.month === month);
}

export function VolunteerOperationsPage() {
  const [loading, setLoading] = useState(true);
  const [sendingFamilyId, setSendingFamilyId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [schoolYear, setSchoolYear] = useState("");
  const [statementMonth, setStatementMonth] = useState(previousMonthKey());
  const [familyStatementMonths, setFamilyStatementMonths] = useState<Record<string, string>>({});
  const [familyFilter, setFamilyFilter] = useState("");
  const [allowedSchoolYears, setAllowedSchoolYears] = useState<string[]>([]);
  const [familySummaries, setFamilySummaries] = useState<FamilySummary[]>([]);
  const [logs, setLogs] = useState<StatementLog[]>([]);
  const [monthlyTrend, setMonthlyTrend] = useState<MonthlyTrend[]>([]);
  const [activeDrilldownKey, setActiveDrilldownKey] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const fn = httpsCallable<
        { schoolYear?: string; statementMonth?: string },
        {
          ok: true;
          schoolYear: string;
          statementMonth: string;
          allowedSchoolYears: string[];
          familySummaries: FamilySummary[];
          logs: StatementLog[];
          monthlyTrend: MonthlyTrend[];
        }
      >(functionsClient(), "getVolunteerOperationsDashboard");

      const res = await fn({
        schoolYear: schoolYear || undefined,
        statementMonth: statementMonth || undefined,
      });

      setSchoolYear(res.data.schoolYear);
      setStatementMonth(res.data.statementMonth);
      setAllowedSchoolYears(res.data.allowedSchoolYears || []);
      setFamilySummaries(res.data.familySummaries || []);
      setLogs(res.data.logs || []);
      setMonthlyTrend(res.data.monthlyTrend || []);
    } catch (e: any) {
      console.error("[VolunteerOperationsPage] load failed", e);
      setErr(e?.message || "Failed to load volunteer operations.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const statementMonthOptions = useMemo(() => {
    const values = new Set<string>();
    schoolYearMonths(schoolYear).forEach((month) => values.add(month));
    monthlyTrend.forEach((row) => values.add(row.month));
    logs.forEach((log) => values.add(log.statementMonth));
    values.add(statementMonth);

    return Array.from(values)
      .filter((month) => /^\d{4}-\d{2}$/.test(month))
      .sort();
  }, [schoolYear, monthlyTrend, logs, statementMonth]);

  const visibleFamilies = useMemo(() => {
    const q = familyFilter.trim().toLowerCase();
    if (!q) return familySummaries;
    return familySummaries.filter((f) =>
      [f.familyName, f.familyCode, f.familyId].join(" ").toLowerCase().includes(q)
    );
  }, [familySummaries, familyFilter]);

  const dashboardMetrics = useMemo(() => {
    const totalFamilies = familySummaries.length;
    const loggingFamilies = familySummaries.filter((f) =>
      f.totals.approvedHours > 0 ||
      f.totals.pendingHours > 0 ||
      f.totals.rejectedHours > 0 ||
      f.statement.monthlyEntriesCount > 0
    ).length;
    const completeFamilies = familySummaries.filter((f) => f.totals.completedGoal).length;
    const nearGoalFamilies = familySummaries.filter((f) => f.flags.nearGoal).length;
    const pendingFamilies = familySummaries.filter((f) => f.totals.pendingHours > 0).length;
    const noActivityFamilies = familySummaries.filter((f) => f.flags.noActivity).length;
    const approvedHours = familySummaries.reduce((sum, f) => sum + f.totals.approvedHours, 0);
    const pendingHours = familySummaries.reduce((sum, f) => sum + f.totals.pendingHours, 0);
    const goalHours = familySummaries.reduce((sum, f) => sum + f.totals.goalHours, 0);
    const monthHours = familySummaries.reduce((sum, f) => sum + f.statement.monthlyApprovedHours, 0);
    const statementLogsForMonth = logs.filter((log) => log.statementMonth === statementMonth);
    const sentStatements = statementLogsForMonth.filter((log) => log.status === "sent").length;
    const failedStatements = statementLogsForMonth.filter((log) => log.status === "failed").length;
    const skippedStatements = statementLogsForMonth.filter((log) => log.status === "skipped").length;
    const topContributors = [...familySummaries]
      .filter((f) => f.totals.approvedHours > 0 || f.totals.pendingHours > 0)
      .sort((a, b) => b.totals.approvedHours - a.totals.approvedHours)
      .slice(0, 6);
    const attentionFamilies = [...familySummaries]
      .filter((f) => f.totals.pendingHours > 0 || f.flags.nearGoal || f.statement.lastLog?.status === "failed" || f.statement.lastLog?.status === "skipped")
      .sort((a, b) => b.totals.pendingHours - a.totals.pendingHours || a.totals.remainingHours - b.totals.remainingHours)
      .slice(0, 6);

    return {
      totalFamilies,
      loggingFamilies,
      completeFamilies,
      nearGoalFamilies,
      pendingFamilies,
      noActivityFamilies,
      approvedHours: Math.round(approvedHours * 100) / 100,
      pendingHours: Math.round(pendingHours * 100) / 100,
      goalHours: Math.round(goalHours * 100) / 100,
      monthHours: Math.round(monthHours * 100) / 100,
      statementLogsForMonth,
      sentStatements,
      failedStatements,
      skippedStatements,
      topContributors,
      attentionFamilies,
    };
  }, [familySummaries, logs, statementMonth]);

  const activeDrilldown = useMemo<Drilldown | null>(() => {
    if (!activeDrilldownKey) return null;

    const monthLogFilter = (status?: StatementLog["status"]) =>
      logs.filter((log) => log.statementMonth === statementMonth && (!status || log.status === status));
    const familyFromLogIds = (matchingLogs: StatementLog[]) => {
      const ids = new Set(matchingLogs.map((log) => log.familyId));
      return familySummaries.filter((family) => ids.has(family.familyId));
    };

    if (activeDrilldownKey === "families-logging") {
      const families = familySummaries.filter((family) =>
        family.totals.approvedHours > 0 ||
        family.totals.pendingHours > 0 ||
        family.totals.rejectedHours > 0 ||
        family.statement.monthlyEntriesCount > 0
      );
      return {
        title: "Families Logging",
        description: `${families.length} families have volunteer activity in ${schoolYear || "this school year"}.`,
        families,
        logs: logs.filter((log) => families.some((family) => family.familyId === log.familyId)),
      };
    }

    if (activeDrilldownKey === "approved-hours") {
      const families = [...familySummaries]
        .filter((family) => family.totals.approvedHours > 0)
        .sort((a, b) => b.totals.approvedHours - a.totals.approvedHours);
      return {
        title: "Approved Hours",
        description: `${hours(dashboardMetrics.approvedHours)} approved hours toward ${hours(dashboardMetrics.goalHours)} total goal hours.`,
        families,
        logs: logs.filter((log) => families.some((family) => family.familyId === log.familyId)),
      };
    }

    if (activeDrilldownKey === "pending-review") {
      const families = [...familySummaries]
        .filter((family) => family.totals.pendingHours > 0)
        .sort((a, b) => b.totals.pendingHours - a.totals.pendingHours);
      return {
        title: "Pending Review",
        description: `${hours(dashboardMetrics.pendingHours)} hours are waiting for review across ${families.length} families.`,
        families,
        logs: logs.filter((log) => families.some((family) => family.familyId === log.familyId)),
      };
    }

    if (activeDrilldownKey === "statement-issues") {
      const matchingLogs = monthLogFilter().filter((log) => log.status === "failed" || log.status === "skipped");
      return {
        title: "Statement Issues",
        description: `${matchingLogs.length} failed or skipped statement logs for ${monthLabel(statementMonth)}.`,
        families: familyFromLogIds(matchingLogs),
        logs: matchingLogs,
      };
    }

    if (activeDrilldownKey === "complete-families") {
      const families = familySummaries.filter((family) => family.totals.completedGoal);
      return {
        title: "Families Complete",
        description: `${families.length} families have met or exceeded their volunteer goal.`,
        families,
        logs: logs.filter((log) => families.some((family) => family.familyId === log.familyId)),
      };
    }

    if (activeDrilldownKey === "near-goal") {
      const families = familySummaries.filter((family) => family.flags.nearGoal);
      return {
        title: "Families Near Goal",
        description: `${families.length} families are within 2 hours of completing their goal.`,
        families,
        logs: logs.filter((log) => families.some((family) => family.familyId === log.familyId)),
      };
    }

    if (activeDrilldownKey === "no-activity") {
      const families = familySummaries.filter((family) => family.flags.noActivity);
      return {
        title: "No Activity",
        description: `${families.length} families have no volunteer entries for ${schoolYear || "the selected year"}.`,
        families,
        logs: [],
      };
    }

    if (activeDrilldownKey.startsWith("statement-status:")) {
      const status = activeDrilldownKey.split(":")[1] as StatementLog["status"];
      const matchingLogs = monthLogFilter(status);
      return {
        title: `${statusBadge(status)} Statements`,
        description: `${matchingLogs.length} ${statusBadge(status).toLowerCase()} statement logs for ${monthLabel(statementMonth)}.`,
        families: familyFromLogIds(matchingLogs),
        logs: matchingLogs,
      };
    }

    if (activeDrilldownKey.startsWith("month:")) {
      const month = activeDrilldownKey.slice("month:".length);
      const families = familySummaries
        .filter((family) => {
          const bucket = familyMonthBucket(family, month);
          return bucket && (bucket.entriesCount > 0 || bucket.approvedHours > 0 || bucket.pendingHours > 0);
        })
        .sort((a, b) => (familyMonthBucket(b, month)?.approvedHours || 0) - (familyMonthBucket(a, month)?.approvedHours || 0));
      const matchingLogs = logs.filter((log) => log.statementMonth === month);
      return {
        title: monthLabel(month),
        description: `${families.length} families logged entries and ${matchingLogs.length} statements were recorded for this month.`,
        families,
        logs: matchingLogs,
      };
    }

    if (activeDrilldownKey === "top-contributors") {
      return {
        title: "Top Contributors",
        description: "Families with the highest approved volunteer hour totals.",
        families: dashboardMetrics.topContributors,
        logs: logs.filter((log) => dashboardMetrics.topContributors.some((family) => family.familyId === log.familyId)),
      };
    }

    if (activeDrilldownKey === "needs-attention") {
      return {
        title: "Needs Attention",
        description: "Families with pending hours, near-goal status, or statement issues.",
        families: dashboardMetrics.attentionFamilies,
        logs: logs.filter((log) => dashboardMetrics.attentionFamilies.some((family) => family.familyId === log.familyId)),
      };
    }

    if (activeDrilldownKey.startsWith("family:")) {
      const familyId = activeDrilldownKey.slice("family:".length);
      const family = familySummaries.find((item) => item.familyId === familyId);
      if (!family) return null;
      return {
        title: family.familyName,
        description: `${hours(family.totals.approvedHours)} approved, ${hours(family.totals.pendingHours)} pending, ${hours(family.totals.remainingHours)} remaining.`,
        families: [family],
        logs: logs.filter((log) => log.familyId === family.familyId),
      };
    }

    return null;
  }, [activeDrilldownKey, dashboardMetrics, familySummaries, logs, schoolYear, statementMonth]);

  function handleSchoolYearChange(nextSchoolYear: string) {
    setSchoolYear(nextSchoolYear);
    setActiveDrilldownKey(null);
    setStatementMonth((current) => {
      const months = schoolYearMonths(nextSchoolYear);
      if (months.includes(current)) return current;
      return defaultMonthForSchoolYear(nextSchoolYear);
    });
    setFamilyStatementMonths({});
  }

  async function resendStatement(familyId: string, targetStatementMonth: string) {
    setSendingFamilyId(familyId);
    setErr(null);
    try {
      const fn = httpsCallable<
        { familyId: string; schoolYear?: string; statementMonth: string },
        { ok: true; skipped?: boolean; reason?: string; recipients?: string[]; familyName?: string }
      >(functionsClient(), "resendVolunteerStatementToFamily");

      const res = await fn({ familyId, schoolYear: schoolYear || undefined, statementMonth: targetStatementMonth });

      if (res.data.skipped) {
        alert(`${monthLabel(targetStatementMonth)} statement skipped: ${res.data.reason || "No recipients or no entries for month."}`);
      } else {
        alert(`${monthLabel(targetStatementMonth)} statement sent to: ${(res.data.recipients || []).join(", ")}`);
      }

      await load();
    } catch (e: any) {
      console.error("[VolunteerOperationsPage] resend failed", e);
      setErr(e?.message || "Failed to resend statement.");
    } finally {
      setSendingFamilyId(null);
    }
  }

  return (
    <div className="volunteer-ops-page">
      <div className="card volunteer-ops-header">
        <h2>Volunteer Operations</h2>
        <div className="muted">Family-level statements, delivery logs, and reporting.</div>
        {err ? <div className="error">{err}</div> : null}
      </div>

      <div className="card">
        <h3>Controls</h3>
        <div className="volunteer-controls">
          <label className="muted">
            School Year
            <select className="input" value={schoolYear} onChange={(e) => handleSchoolYearChange(e.target.value)}>
              {allowedSchoolYears.length === 0 ? <option value={schoolYear || ""}>{schoolYear || "Current"}</option> : null}
              {allowedSchoolYears.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </label>

          <label className="muted">
            Statement Month
            <select className="input" value={statementMonth} onChange={(e) => {
              setStatementMonth(e.target.value);
              setActiveDrilldownKey(null);
            }}>
              {statementMonthOptions.map((month) => (
                <option key={month} value={month}>{monthLabel(month)}</option>
              ))}
            </select>
          </label>

          <label className="muted">
            Family Filter
            <input className="input" value={familyFilter} onChange={(e) => setFamilyFilter(e.target.value)} placeholder="Search by family name, code, or ID" />
          </label>

          <div className="volunteer-control-action">
            <button className="btn" onClick={load} disabled={loading}>{loading ? "Loading..." : "Refresh"}</button>
          </div>
        </div>
      </div>

      <section className="volunteer-kpi-grid" aria-label="Volunteer operations summary">
        <KpiTile label="Families logging" value={`${pct(dashboardMetrics.loggingFamilies, dashboardMetrics.totalFamilies)}%`} detail={`${dashboardMetrics.loggingFamilies} of ${dashboardMetrics.totalFamilies} families`} tone="green" onClick={() => setActiveDrilldownKey("families-logging")} />
        <KpiTile label="Approved hours" value={hours(dashboardMetrics.approvedHours)} detail={`${pct(dashboardMetrics.approvedHours, dashboardMetrics.goalHours)}% of all family goals`} tone="blue" onClick={() => setActiveDrilldownKey("approved-hours")} />
        <KpiTile label="Pending review" value={hours(dashboardMetrics.pendingHours)} detail={`${dashboardMetrics.pendingFamilies} families waiting`} tone={dashboardMetrics.pendingHours > 0 ? "gold" : "green"} onClick={() => setActiveDrilldownKey("pending-review")} />
        <KpiTile label="Statement issues" value={dashboardMetrics.failedStatements + dashboardMetrics.skippedStatements} detail={`${dashboardMetrics.failedStatements} failed, ${dashboardMetrics.skippedStatements} skipped for ${statementMonth}`} tone={dashboardMetrics.failedStatements > 0 ? "red" : dashboardMetrics.skippedStatements > 0 ? "gold" : "green"} onClick={() => setActiveDrilldownKey("statement-issues")} />
      </section>

      {activeDrilldown ? (
        <div className="card dashboard-panel volunteer-panel volunteer-drilldown">
          <div className="volunteer-section-head">
            <div>
              <h3>{activeDrilldown.title}</h3>
              <p className="muted">{activeDrilldown.description}</p>
            </div>
            <button className="btn secondary small" type="button" onClick={() => setActiveDrilldownKey(null)}>Close</button>
          </div>

          <div className="volunteer-drilldown-summary">
            <div><span>Families</span><b>{activeDrilldown.families.length}</b></div>
            <div><span>Approved</span><b>{hours(activeDrilldown.families.reduce((sum, family) => sum + family.totals.approvedHours, 0))}</b></div>
            <div><span>Pending</span><b>{hours(activeDrilldown.families.reduce((sum, family) => sum + family.totals.pendingHours, 0))}</b></div>
            <div><span>Logs</span><b>{activeDrilldown.logs.length}</b></div>
          </div>

          {activeDrilldown.families.length ? (
            <div className="volunteer-drilldown-list">
              {activeDrilldown.families.slice(0, 12).map((family) => (
                <button className="volunteer-drilldown-family" type="button" key={family.familyId} onClick={() => setActiveDrilldownKey(`family:${family.familyId}`)}>
                  <span>
                    <b>{family.familyName}</b>
                    <small>{family.familyCode || family.familyId}</small>
                  </span>
                  <strong>{hours(family.totals.approvedHours)} approved</strong>
                </button>
              ))}
            </div>
          ) : (
            <div className="muted">No families matched this drilldown.</div>
          )}

          {activeDrilldown.logs.length ? (
            <div className="volunteer-log-wrap">
              <table className="volunteer-log-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Family</th>
                    <th>Month</th>
                    <th>Status</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {activeDrilldown.logs.slice(0, 12).map((log) => (
                    <tr key={log.id}>
                      <td>{tsToString(log.createdAt)}</td>
                      <td>{log.familyName}</td>
                      <td>{log.statementMonth}</td>
                      <td><StatusPill status={log.status} /></td>
                      <td>{log.reason || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="volunteer-insights-grid">
        <div className="card dashboard-panel volunteer-panel">
          <div className="volunteer-panel-head">
            <div>
              <h3>Participation</h3>
              <p className="muted">Family logging and goal progress for {schoolYear || "the selected year"}.</p>
            </div>
            <Donut value={dashboardMetrics.loggingFamilies} total={dashboardMetrics.totalFamilies} label="logging" tone="green" />
          </div>
          <div className="volunteer-meter-stack">
            <MeterBar label="Families logging" value={dashboardMetrics.loggingFamilies} total={dashboardMetrics.totalFamilies} detail={`${dashboardMetrics.loggingFamilies} / ${dashboardMetrics.totalFamilies}`} tone="green" onClick={() => setActiveDrilldownKey("families-logging")} />
            <MeterBar label="Families complete" value={dashboardMetrics.completeFamilies} total={dashboardMetrics.totalFamilies} detail={`${dashboardMetrics.completeFamilies} / ${dashboardMetrics.totalFamilies}`} tone="blue" onClick={() => setActiveDrilldownKey("complete-families")} />
            <MeterBar label="Families near goal" value={dashboardMetrics.nearGoalFamilies} total={dashboardMetrics.totalFamilies} detail={`${dashboardMetrics.nearGoalFamilies} / ${dashboardMetrics.totalFamilies}`} tone="gold" onClick={() => setActiveDrilldownKey("near-goal")} />
            <MeterBar label="No activity" value={dashboardMetrics.noActivityFamilies} total={dashboardMetrics.totalFamilies} detail={`${dashboardMetrics.noActivityFamilies} / ${dashboardMetrics.totalFamilies}`} tone="red" onClick={() => setActiveDrilldownKey("no-activity")} />
          </div>
        </div>

        <div className="card dashboard-panel volunteer-panel">
          <div className="volunteer-panel-head">
            <div>
              <h3>Statement Health</h3>
              <p className="muted">Delivery status for {statementMonth} statements.</p>
            </div>
            <Donut value={dashboardMetrics.sentStatements} total={Math.max(dashboardMetrics.statementLogsForMonth.length, 1)} label="sent" tone={dashboardMetrics.failedStatements > 0 ? "red" : "green"} />
          </div>
          <div className="volunteer-meter-stack">
            <MeterBar label="Sent" value={dashboardMetrics.sentStatements} total={Math.max(dashboardMetrics.statementLogsForMonth.length, 1)} detail={`${dashboardMetrics.sentStatements}`} tone="green" onClick={() => setActiveDrilldownKey("statement-status:sent")} />
            <MeterBar label="Failed" value={dashboardMetrics.failedStatements} total={Math.max(dashboardMetrics.statementLogsForMonth.length, 1)} detail={`${dashboardMetrics.failedStatements}`} tone="red" onClick={() => setActiveDrilldownKey("statement-status:failed")} />
            <MeterBar label="Skipped" value={dashboardMetrics.skippedStatements} total={Math.max(dashboardMetrics.statementLogsForMonth.length, 1)} detail={`${dashboardMetrics.skippedStatements}`} tone="gold" onClick={() => setActiveDrilldownKey("statement-status:skipped")} />
          </div>
        </div>
      </div>

      <div className="volunteer-insights-grid">
        <div className="card dashboard-panel volunteer-panel">
          <h3>Year Trend</h3>
          {monthlyTrend.length === 0 ? (
            <div className="muted">No trend data for this school year.</div>
          ) : (
            <div className="volunteer-trend-bars">
              {monthlyTrend.map((row) => {
                const maxHours = Math.max(...monthlyTrend.map((item) => item.approvedHours + item.pendingHours), 1);
                const approvedHeight = Math.max(8, Math.round((row.approvedHours / maxHours) * 116));
                const pendingHeight = row.pendingHours > 0 ? Math.max(6, Math.round((row.pendingHours / maxHours) * 116)) : 0;
                return (
                  <div className="volunteer-trend-column" key={row.month}>
                    <button className="volunteer-trend-stack" type="button" title={`${row.month}: ${hours(row.approvedHours)} approved, ${hours(row.pendingHours)} pending`} onClick={() => setActiveDrilldownKey(`month:${row.month}`)}>
                      {pendingHeight ? <span className="pending" style={{ height: pendingHeight }} /> : null}
                      <span className="approved" style={{ height: approvedHeight }} />
                    </button>
                    <b>{row.month.slice(5)}</b>
                    <small>{row.entriesCount} entries</small>
                  </div>
                );
              })}
            </div>
          )}
          <div className="volunteer-chart-legend">
            <span><i className="approved" /> Approved</span>
            <span><i className="pending" /> Pending</span>
            <b>{hours(dashboardMetrics.monthHours)} approved hours in {statementMonth}</b>
          </div>
        </div>

        <div className="card dashboard-panel volunteer-panel">
          <h3>Top Contributors</h3>
          {dashboardMetrics.topContributors.length === 0 ? (
            <div className="muted">No approved volunteer hours yet.</div>
          ) : (
            <div className="volunteer-bar-list">
              {dashboardMetrics.topContributors.map((family) => (
                <button className="volunteer-bar-row volunteer-clickable-row" type="button" key={family.familyId} onClick={() => setActiveDrilldownKey(`family:${family.familyId}`)}>
                  <div className="volunteer-bar-row-top">
                    <b>{family.familyName}</b>
                    <strong>{hours(family.totals.approvedHours)}</strong>
                  </div>
                  <div className="volunteer-meter-track">
                    <div className="volunteer-meter-fill volunteer-meter-blue" style={{ width: `${pct(family.totals.approvedHours, Math.max(dashboardMetrics.topContributors[0]?.totals.approvedHours || 1, 1))}%` }} />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {dashboardMetrics.attentionFamilies.length ? (
        <div className="card dashboard-panel volunteer-panel">
          <h3>Needs Attention</h3>
          <div className="volunteer-attention-list">
            {dashboardMetrics.attentionFamilies.map((family) => (
              <button className="volunteer-attention-item" type="button" key={family.familyId} onClick={() => setActiveDrilldownKey(`family:${family.familyId}`)}>
                <b>{family.familyName}</b>
                <span>
                  {family.totals.pendingHours > 0 ? `${hours(family.totals.pendingHours)} pending` : null}
                  {family.flags.nearGoal ? `${family.totals.pendingHours > 0 ? " - " : ""}${hours(family.totals.remainingHours)} hours remaining` : null}
                  {family.statement.lastLog?.status === "failed" || family.statement.lastLog?.status === "skipped" ? `${family.totals.pendingHours > 0 || family.flags.nearGoal ? " - " : ""}statement ${family.statement.lastLog.status}` : null}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="card dashboard-panel volunteer-panel">
        <div className="volunteer-section-head">
          <div>
            <h3>Families</h3>
            <p className="muted">{visibleFamilies.length} of {familySummaries.length} families shown</p>
          </div>
        </div>

        <div className="volunteer-family-grid">
          {visibleFamilies.map((f) => {
            const progress = pct(f.totals.approvedHours, f.totals.goalHours);
            const recipients = f.statement.lastLog?.recipients || f.statement.recipients || [];
            const selectedFamilyMonth = familyStatementMonths[f.familyId] || statementMonth;
            return (
              <article className="volunteer-family-card" key={f.familyId}>
                <div className="volunteer-family-top">
                  <div>
                    <h4>{f.familyName}</h4>
                    <div className="muted"><code>{f.familyCode || "No code"}</code> - {f.familyId}</div>
                  </div>
                  <span className={`volunteer-pill ${f.totals.completedGoal ? "volunteer-pill-ok" : f.flags.nearGoal ? "volunteer-pill-warn" : "volunteer-pill-neutral"}`}>
                    {f.totals.completedGoal ? "Complete" : f.flags.nearGoal ? "Near goal" : f.flags.noActivity ? "No activity" : "Active"}
                  </span>
                </div>

                <MeterBar label="Goal progress" value={f.totals.approvedHours} total={Math.max(f.totals.goalHours, 1)} detail={`${progress}%`} tone={f.totals.completedGoal ? "green" : "blue"} />

                <div className="volunteer-family-stats">
                  <div><span>Approved</span><b>{hours(f.totals.approvedHours)}</b></div>
                  <div><span>Pending</span><b>{hours(f.totals.pendingHours)}</b></div>
                  <div><span>Goal</span><b>{hours(f.totals.goalHours)}</b></div>
                  <div><span>Remaining</span><b>{hours(f.totals.remainingHours)}</b></div>
                  <div><span>{statementMonth} hours</span><b>{hours(f.statement.monthlyApprovedHours)}</b></div>
                  <div><span>{statementMonth} entries</span><b>{f.statement.monthlyEntriesCount}</b></div>
                </div>

                <div className="volunteer-family-meta">
                  <div>
                    <span>Last statement</span>
                    {f.statement.lastLog ? (
                      <div className="volunteer-inline">
                        <StatusPill status={f.statement.lastLog.status} />
                        <small>{tsToString(f.statement.lastLog.createdAt)}</small>
                      </div>
                    ) : (
                      <b>Not sent</b>
                    )}
                  </div>
                  <div>
                    <span>Recipients</span>
                    <b title={recipients.join(", ")}>{compactList(recipients)}</b>
                  </div>
                </div>

                <div className="volunteer-family-actions">
                  <button className="btn secondary" type="button" onClick={() => setActiveDrilldownKey(`family:${f.familyId}`)}>
                    Details
                  </button>
                  <select
                    className="input volunteer-family-month-select"
                    value={selectedFamilyMonth}
                    onChange={(e) => setFamilyStatementMonths((prev) => ({ ...prev, [f.familyId]: e.target.value }))}
                    aria-label={`Statement month for ${f.familyName}`}
                  >
                    {statementMonthOptions.map((month) => (
                      <option key={month} value={month}>{monthLabel(month)}</option>
                    ))}
                  </select>
                  <button className="btn secondary" disabled={sendingFamilyId === f.familyId} onClick={() => resendStatement(f.familyId, selectedFamilyMonth)}>
                    {sendingFamilyId === f.familyId ? "Sending..." : "Send / Resend"}
                  </button>
                </div>
              </article>
            );
          })}
          {visibleFamilies.length === 0 ? <div className="muted">No families matched.</div> : null}
        </div>
      </div>

      <div className="card dashboard-panel volunteer-panel">
        <h3>Statement Logs</h3>
        {logs.length === 0 ? (
          <div className="muted">No statement logs found.</div>
        ) : (
          <div className="volunteer-log-wrap">
            <table className="volunteer-log-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Family</th>
                  <th>Month</th>
                  <th>Status</th>
                  <th>Trigger</th>
                  <th>Recipients</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td>{tsToString(log.createdAt)}</td>
                    <td>{log.familyName}</td>
                    <td>{log.statementMonth}</td>
                    <td><StatusPill status={log.status} /></td>
                    <td>{log.triggerType}</td>
                    <td>{log.recipients.join(", ") || "-"}</td>
                    <td>{log.reason || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
